/**
 * Copy of the BTG Pactual connection surfaces (ADR 0032): the integration card and the lapse
 * banner of a bank account. The Integrations screen (admin) may name the bank; the bank account
 * page is operator-facing, so its banner says "gateway de pagamento" instead.
 */
export const btgConnectionPtBr = {
  title: 'BTG Pactual',
  description:
    'Conecte cada empresa ao BTG Pactual uma vez, com o login de um usuário com perfil Operador no BTG. Depois disso, os pagamentos enviados pelas contas BTG ficam aguardando aprovação no app do banco.',
  notConfigured: {
    title: 'Integração indisponível neste ambiente',
    description:
      'O aplicativo parceiro do BTG ainda não foi configurado aqui. Fale com o suporte da Alchemy.',
  },
  permissionBanner: {
    title_one: 'Uma empresa tem um módulo ligado sem permissão no BTG',
    title_other: '{{count}} empresas têm módulos ligados sem permissão no BTG',
    description:
      'O BTG não concedeu a permissão de um módulo que está ligado, e ele para até a empresa ser reconectada. Reconecte e aceite a permissão no BTG.',
  },
  lapsedBanner: {
    title_one: 'Uma conexão com o BTG Pactual expirou',
    title_other: '{{count}} conexões com o BTG Pactual expiraram',
    description:
      'Os envios de pagamento pelas contas BTG dessas empresas estão bloqueados até alguém reconectar.',
  },
  list: {
    title: 'Empresas',
    description: 'Uma conexão por empresa. O CNPJ autorizado no BTG precisa ser o da empresa.',
    empty: {
      title: 'Nenhuma empresa cadastrada',
      description: 'Cadastre uma empresa com CNPJ para conectá-la ao BTG Pactual.',
    },
    cnpj: 'CNPJ {{cnpj}}',
    noCnpj: 'Sem CNPJ cadastrado',
    loading: 'Carregando as empresas',
    loadError: 'Não foi possível carregar as empresas.',
  },
  status: {
    NOT_CONNECTED: 'Não conectada',
    PENDING: 'Pendente',
    ACTIVE: 'Conectada',
    LAPSED: 'Expirada',
    REVOKED: 'Desconectada',
  },
  detail: {
    connectedAt: 'Conectada em {{date}}',
    lapsedAt: 'Expirou em {{date}}',
    refreshFailing:
      'A renovação está falhando desde {{date}}; a conexão expira se continuar por 10 dias.',
  },
  lapseReason: {
    REFRESH_GRANT_REFUSED: 'O BTG recusou a renovação: o consentimento foi revogado ou expirou.',
    REFRESH_FAILING_TEN_DAYS: 'A renovação falhou por 10 dias seguidos.',
    PROVIDER_REFUSED_TOKEN: 'O BTG recusou o acesso ao processar um pagamento.',
  },
  header: {
    connected: '{{connected}} de {{total}} empresas conectadas',
  },
  accounts: {
    title: 'Contas no banco',
    bankName: 'BTG',
    listFor: 'Contas de {{entity}} no BTG',
    branchAccount: 'Ag. {{branch}} · Conta {{number}}',
    none: 'Nenhuma conta encontrada no banco.',
    noneLapsed: 'Nenhuma conta cadastrada aqui. Reconecte para ver as contas do banco.',
    unavailable: 'Contas do banco indisponíveis agora.',
    register: 'Cadastrar',
    registerFor: 'Cadastrar a conta {{account}}',
  },
  actions: {
    connect: 'Conectar',
    reconnect: 'Reconectar',
    disconnect: 'Desconectar',
    redirecting: 'Abrindo o BTG…',
    dismiss: 'Fechar',
  },
  disconnectDialog: {
    title: 'Desconectar {{entity}} do BTG Pactual?',
    description:
      'Os envios de pagamento pelas contas BTG desta empresa ficam bloqueados até uma nova conexão. Para encerrar o consentimento também no banco, revogue o aplicativo no BTG.',
    confirm: 'Desconectar',
    cancel: 'Cancelar',
    success: '{{entity}} foi desconectada do BTG Pactual.',
  },
  result: {
    connected: {
      title: 'Empresa conectada ao BTG Pactual',
      description: 'Os pagamentos pelas contas BTG desta empresa já podem ser enviados.',
    },
    state_invalid: {
      title: 'A conexão não foi concluída',
      description:
        'O link de retorno do BTG expirou ou já foi usado. Clique em Conectar e faça o login de novo.',
    },
    consent_denied: {
      title: 'A autorização foi recusada no BTG',
      description: 'Nada foi salvo. Clique em Conectar para tentar de novo.',
    },
    cnpj_mismatch: {
      title: 'O CNPJ autorizado não é o da empresa',
      description:
        'Nada foi salvo. No BTG, escolha a mesma empresa (mesmo CNPJ) cadastrada aqui. Se a lista de empresas não aparecer, revogue o aplicativo no BTG e conecte de novo.',
    },
    company_unresolved: {
      title: 'O BTG não informou qual empresa foi autorizada',
      description: 'Nada foi salvo. Tente de novo; se continuar, fale com o suporte.',
    },
    entity_cnpj_missing: {
      title: 'A empresa não tem CNPJ cadastrado',
      description: 'Cadastre o CNPJ da empresa e conecte de novo.',
    },
    exchange_failed: {
      title: 'O BTG não confirmou a conexão',
      description: 'Nada foi salvo. Tente de novo em alguns minutos.',
    },
    not_configured: {
      title: 'Integração indisponível neste ambiente',
      description: 'O aplicativo parceiro do BTG ainda não foi configurado aqui.',
    },
  },
  errors: {
    start: 'Não foi possível abrir o login do BTG. Tente de novo.',
    disconnect: 'Não foi possível desconectar. Tente de novo.',
  },
  dda: {
    title: 'Consulta de débitos (DDA)',
    enable: 'Aderir à consulta de débitos (DDA) de {{entity}}',
    pause: 'Pausar a consulta de débitos (DDA) de {{entity}}',
    stateOn: 'Aderida',
    stateOff: 'Pausada. Nada é consultado enquanto isso.',
    lastSync: 'Última consulta em {{date}}',
    reconnect: 'Reconectar',
    status: {
      UNKNOWN: 'Ainda não consultado',
      ACTIVE: 'Ativa',
      AWAITING_BANK: 'Aguardando a empresa habilitar o DDA no banco',
      MISSING_SCOPE: 'A conexão não tem a permissão de débitos — reconecte',
      REFUSED: 'Recusada pelo banco',
      UNAVAILABLE: 'Indisponível neste gateway de pagamento',
    },
    errors: {
      load: 'Não foi possível carregar a adesão ao DDA. Recarregue a página.',
      save: 'Não foi possível salvar a adesão ao DDA. Tente de novo.',
    },
  },
  permissions: {
    label: 'Permissões no BTG',
    group: 'Permissões que o BTG concedeu a {{entity}}',
    module: {
      apiPayments: 'Pagamentos',
      statement: 'Extrato',
      dda: 'DDA',
    },
    missing: '{{module}} · sem permissão',
  },
  apiPayments: {
    label: 'Pagamentos pela API',
    switch: 'Pagamentos pela API de {{account}}',
    notRegistered: 'Cadastre a conta para ligar os pagamentos pela API',
    notLinked: 'A conta está inativa ou ainda não foi vinculada ao BTG.',
    missingPermission: 'Falta a permissão de pagamentos no BTG. Reconecte a empresa.',
    lapsedOff: 'Conexão expirada · reconecte para ligar',
    allOn: 'Ligar pagamentos em todas as contas',
    allOnFailed_one: '{{count}} conta não foi ligada; o motivo está na conta.',
    allOnFailed_other: '{{count}} contas não foram ligadas; o motivo está em cada conta.',
    errors: {
      PROVIDER_SWITCH_BLOCKED:
        'Um envio desta conta ainda está em andamento. Tente de novo depois.',
      PAYMENT_SETUP_ERROR:
        'O BTG não concluiu a configuração de pagamentos desta conta. Abra a conta para ver o motivo.',
      BTG_ACCOUNT_MODULES_EMPTY: 'Nada foi alterado. Tente de novo.',
      BTG_ACCOUNT_NOT_LINKED: 'A conta não está ativa ou vinculada ao BTG. Recarregue a página.',
      BTG_CONNECTION_NOT_ACTIVE: 'A conexão com o BTG não está ativa. Reconecte a empresa.',
      PROVIDER_CHANGED:
        'A conta passou a outro gateway de pagamento nesse meio-tempo; nada foi alterado.',
      other: 'Não foi possível salvar os pagamentos pela API. Tente de novo.',
    },
  },
  modules: {
    dda: {
      label: 'Consulta de débitos (DDA)',
      switch: 'Consulta de débitos (DDA) de {{entity}}',
      subscribed: 'Aderida · {{status}}',
      subscribedSynced: 'Aderida · última consulta em {{date}}',
      paused: 'Pausada · nada é consultado enquanto isso',
      notSubscribed: 'Não aderida',
      missingPermission: 'Falta a permissão de DDA no BTG. Reconecte a empresa.',
      loading: 'Carregando a adesão ao DDA',
    },
  },
} as const;

export const btgConnectionEn = {
  title: 'BTG Pactual',
  description:
    'Connect each company to BTG Pactual once, with the login of a user who has the Operator profile at BTG. After that, payments sent from BTG accounts wait for approval in the bank app.',
  notConfigured: {
    title: 'Integration unavailable in this environment',
    description: 'The BTG partner app is not configured here yet. Contact Alchemy support.',
  },
  permissionBanner: {
    title_one: 'One company has a module on without its BTG permission',
    title_other: '{{count}} companies have modules on without their BTG permission',
    description:
      'BTG did not grant the permission of a module that is on, and it stops until the company is reconnected. Reconnect and accept the permission at BTG.',
  },
  lapsedBanner: {
    title_one: 'One BTG Pactual connection lapsed',
    title_other: '{{count}} BTG Pactual connections lapsed',
    description:
      "Payment sends from these companies' BTG accounts are blocked until someone reconnects.",
  },
  list: {
    title: 'Companies',
    description: "One connection per company. The CNPJ authorized at BTG must be the company's.",
    empty: {
      title: 'No companies yet',
      description: 'Add a company with a CNPJ to connect it to BTG Pactual.',
    },
    cnpj: 'CNPJ {{cnpj}}',
    noCnpj: 'No CNPJ registered',
    loading: 'Loading the companies',
    loadError: 'Could not load the companies.',
  },
  status: {
    NOT_CONNECTED: 'Not connected',
    PENDING: 'Pending',
    ACTIVE: 'Connected',
    LAPSED: 'Lapsed',
    REVOKED: 'Disconnected',
  },
  detail: {
    connectedAt: 'Connected on {{date}}',
    lapsedAt: 'Lapsed on {{date}}',
    refreshFailing:
      'Renewal has been failing since {{date}}; the connection lapses if it goes on for 10 days.',
  },
  lapseReason: {
    REFRESH_GRANT_REFUSED: 'BTG refused the renewal: the consent was revoked or expired.',
    REFRESH_FAILING_TEN_DAYS: 'Renewal failed for 10 days in a row.',
    PROVIDER_REFUSED_TOKEN: 'BTG refused access while processing a payment.',
  },
  header: {
    connected: '{{connected}} of {{total}} companies connected',
  },
  accounts: {
    title: 'Accounts at the bank',
    bankName: 'BTG',
    listFor: 'Accounts of {{entity}} at BTG',
    branchAccount: 'Branch {{branch}} · Account {{number}}',
    none: 'No account found at the bank.',
    noneLapsed: 'No account registered here. Reconnect to see the accounts at the bank.',
    unavailable: 'Bank accounts unavailable right now.',
    register: 'Register',
    registerFor: 'Register account {{account}}',
  },
  actions: {
    connect: 'Connect',
    reconnect: 'Reconnect',
    disconnect: 'Disconnect',
    redirecting: 'Opening BTG…',
    dismiss: 'Dismiss',
  },
  disconnectDialog: {
    title: 'Disconnect {{entity}} from BTG Pactual?',
    description:
      "Payment sends from this company's BTG accounts are blocked until a new connection. To end the consent at the bank as well, revoke the app at BTG.",
    confirm: 'Disconnect',
    cancel: 'Cancel',
    success: '{{entity}} was disconnected from BTG Pactual.',
  },
  result: {
    connected: {
      title: 'Company connected to BTG Pactual',
      description: "Payments from this company's BTG accounts can be sent now.",
    },
    state_invalid: {
      title: 'The connection was not completed',
      description:
        'The return link from BTG expired or was already used. Click Connect and log in again.',
    },
    consent_denied: {
      title: 'The authorization was refused at BTG',
      description: 'Nothing was saved. Click Connect to try again.',
    },
    cnpj_mismatch: {
      title: "The authorized CNPJ is not the company's",
      description:
        'Nothing was saved. At BTG, choose the same company (same CNPJ) registered here. If the company list does not appear, revoke the app at BTG and connect again.',
    },
    company_unresolved: {
      title: 'BTG did not say which company was authorized',
      description: 'Nothing was saved. Try again; if it persists, contact support.',
    },
    entity_cnpj_missing: {
      title: 'The company has no CNPJ registered',
      description: "Register the company's CNPJ and connect again.",
    },
    exchange_failed: {
      title: 'BTG did not confirm the connection',
      description: 'Nothing was saved. Try again in a few minutes.',
    },
    not_configured: {
      title: 'Integration unavailable in this environment',
      description: 'The BTG partner app is not configured here yet.',
    },
  },
  errors: {
    start: 'Could not open the BTG login. Try again.',
    disconnect: 'Could not disconnect. Try again.',
  },
  dda: {
    title: 'Debit lookup (DDA)',
    enable: 'Opt {{entity}} in to the debit lookup (DDA)',
    pause: 'Pause the debit lookup (DDA) of {{entity}}',
    stateOn: 'Opted in',
    stateOff: 'Paused. Nothing is looked up meanwhile.',
    lastSync: 'Last lookup on {{date}}',
    reconnect: 'Reconnect',
    status: {
      UNKNOWN: 'Not looked up yet',
      ACTIVE: 'Active',
      AWAITING_BANK: 'Waiting for the company to enable DDA at the bank',
      MISSING_SCOPE: 'The connection lacks the debit permission — reconnect',
      REFUSED: 'Refused by the bank',
      UNAVAILABLE: 'Unavailable on this payment gateway',
    },
    errors: {
      load: 'Could not load the DDA opt-in. Reload the page.',
      save: 'Could not save the DDA opt-in. Try again.',
    },
  },
  permissions: {
    label: 'Permissions at BTG',
    group: 'Permissions BTG granted to {{entity}}',
    module: {
      apiPayments: 'Payments',
      statement: 'Statement',
      dda: 'DDA',
    },
    missing: '{{module}} · no permission',
  },
  apiPayments: {
    label: 'Payments by API',
    switch: 'Payments by API for {{account}}',
    notRegistered: 'Register the account to turn on payments by API',
    notLinked: 'The account is inactive or not linked to BTG yet.',
    missingPermission: 'The BTG payments permission is missing. Reconnect the company.',
    lapsedOff: 'Connection lapsed · reconnect to turn it on',
    allOn: 'Turn on payments for every account',
    allOnFailed_one: '{{count}} account was not turned on; the reason is on the account.',
    allOnFailed_other: '{{count}} accounts were not turned on; the reason is on each account.',
    errors: {
      PROVIDER_SWITCH_BLOCKED: 'A send from this account is still in progress. Try again later.',
      PAYMENT_SETUP_ERROR:
        "BTG did not finish this account's payment setup. Open the account to see why.",
      BTG_ACCOUNT_MODULES_EMPTY: 'Nothing was changed. Try again.',
      BTG_ACCOUNT_NOT_LINKED: 'The account is not active or not linked to BTG. Reload the page.',
      BTG_CONNECTION_NOT_ACTIVE: 'The BTG connection is not active. Reconnect the company.',
      PROVIDER_CHANGED:
        'The account moved to another payment gateway meanwhile; nothing was changed.',
      other: 'Could not save payments by API. Try again.',
    },
  },
  modules: {
    dda: {
      label: 'Debit lookup (DDA)',
      switch: 'Debit lookup (DDA) for {{entity}}',
      subscribed: 'Opted in · {{status}}',
      subscribedSynced: 'Opted in · last lookup on {{date}}',
      paused: 'Paused · nothing is looked up meanwhile',
      notSubscribed: 'Not opted in',
      missingPermission: 'The BTG DDA permission is missing. Reconnect the company.',
      loading: 'Loading the DDA opt-in',
    },
  },
} as const;
